import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  serviceAgentIntroductionService,
  IntroduceInvestorLookups,
  IntroduceInvestorRequest,
} from '../../../services/serviceAgentIntroduction.service';
import { IntroduceEmailCheck } from '../../../models/ServiceAgentIntroductionDto';
import { IntroOption } from '../../../models/ServiceAgentIntroductionDto';
import '../../../components/LoadingSpinner/LoadingSpinner.scss';
import { SearchableSelect, SearchableOption } from '../../../components/SearchableSelect/SearchableSelect';
import { getPermissionErrorMessage } from '../../../utils/apiClient';
import './IntroduceInvestor.scss';

type FormState = {
  investorTypeGuid: string;
  nationalityGuid: string;
  countryOfResidenceGuid: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  isdCodeGuid: string;
  mobile: string;
  useFacilonServiceAgent: boolean;
  facilonServiceAgentGuid: string;
  investmentRouteGuid: string;
  productGuid: string;
  brokerBankGuid: string;
  planGuid: string;
};

const EMPTY: FormState = {
  investorTypeGuid: '', nationalityGuid: '', countryOfResidenceGuid: '',
  firstName: '', middleName: '', lastName: '', email: '', isdCodeGuid: '', mobile: '',
  useFacilonServiceAgent: false, facilonServiceAgentGuid: '',
  investmentRouteGuid: '', productGuid: '', brokerBankGuid: '', planGuid: '',
};

/**
 * Introduce Investor — the Client Executive Agent's version of the Power Apps
 * "Broker – Investor (Individual) → Create" form.
 *
 * The service provider and the executive agent are shown read-only because the API takes them
 * from the signed-in profile; nothing about them is submitted from here.
 */
export const IntroduceInvestor: React.FC = () => {
  const navigate = useNavigate();
  const [lookups, setLookups] = useState<IntroduceInvestorLookups | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [emailCheck, setEmailCheck] = useState<IntroduceEmailCheck | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await serviceAgentIntroductionService.getLookups();
        setLookups(data);
        // Direct is the portal's default route; preselect it when present.
        const direct = data.investmentRoutes.find(r => r.name?.toLowerCase() === 'direct');
        if (direct) setForm(f => ({ ...f, investmentRouteGuid: direct.id }));
      } catch (err: any) {
        const permissionError = getPermissionErrorMessage(err);
        const message = err?.response?.data?.message;
        if (err?.response?.status === 409 && message) {
          setBlocked(message);
        } else if (permissionError) {
          toast.error(permissionError);
        } else {
          toast.error(message || 'Failed to load the form');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k: keyof FormState, v: string | boolean) => {
    setForm(f => ({ ...f, [k]: v } as FormState));
    setErrors(e => (e[k] ? { ...e, [k]: '' } : e));
  };

  /*
   * Tell the agent what a known email means, before they submit. Advisory only: introducing an
   * existing investor is how they are offered a second product, and the API allows it. Debounced
   * so it fires once the address stops changing rather than on every keystroke.
   */
  useEffect(() => {
    const email = form.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailCheck(null);
      setCheckingEmail(false);
      return;
    }
    let cancelled = false;
    setCheckingEmail(true);
    const timer = window.setTimeout(async () => {
      try {
        const result = await serviceAgentIntroductionService.checkEmail(email, form.productGuid);
        if (!cancelled) setEmailCheck(result.status === 'NEW' ? null : result);
      } catch {
        // A failed advisory check must never get in the way of the form.
        if (!cancelled) setEmailCheck(null);
      } finally {
        if (!cancelled) setCheckingEmail(false);
      }
    }, 500);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [form.email, form.productGuid]);

  /*
   * Resident Individual is hidden from the list for now (business call, 2026-09-28) — the
   * option still exists in Dataverse and the RI defaults below are kept, so restoring it is
   * a matter of deleting this filter.
   */
  const investorTypes: IntroOption[] = useMemo(
    () => (lookups?.investorTypes || []).filter(
      t => t.parentId !== '100000000' && !/^resident individual$/i.test(t.name.trim())),
    [lookups],
  );

  const selectedType = useMemo(
    () => lookups?.investorTypes.find(t => t.id === form.investorTypeGuid) || null,
    [lookups, form.investorTypeGuid],
  );

  /*
   * Investor-type defaults, filled only into fields still blank so nothing chosen is
   * overwritten, and every field stays editable:
   *   Resident Individual - Indian, living in India, +91.
   *   NRI                 - Indian by definition; residence and ISD are theirs to pick.
   * Matched by name because the GUIDs differ per Dataverse environment.
   */
  useEffect(() => {
    if (!lookups || !selectedType) return;
    const typeName = selectedType.name.trim();
    const isResident = selectedType.parentId === '100000000' || /^resident individual$/i.test(typeName);
    const isNri = selectedType.parentId === '100000003' || /^nri$/i.test(typeName);
    if (!isResident && !isNri) return;
    const find = (list: IntroOption[], test: (o: IntroOption) => boolean) =>
      list.find(test)?.id || '';
    const nationality = find(lookups.nationalities,
      o => /^indian$/i.test(o.name.trim()) || (o.parentId || '').trim().toUpperCase() === 'IN');
    const residence = find(lookups.countriesOfResidence, o => /^india$/i.test(o.name.trim()));
    const isd = find(lookups.isdCodes, o => /^\+?91\s+india$/i.test(o.name.trim()));
    setForm(f => ({
      ...f,
      nationalityGuid: f.nationalityGuid || nationality,
      countryOfResidenceGuid: f.countryOfResidenceGuid || (isResident ? residence : ''),
      isdCodeGuid: f.isdCodeGuid || (isResident ? isd : ''),
    }));
    setErrors(e => (e.nationalityGuid && nationality ? { ...e, nationalityGuid: '' } : e));
  }, [selectedType, lookups]);

  /**
   * Products carry `ss_applicableto` (a "; " separated list of investor-type names) in parentId.
   * Until a type is chosen, show everything the provider has a plan for.
   */
  const products: IntroOption[] = useMemo(() => {
    const all = lookups?.products || [];
    // Both sides are Dataverse option CODES, not labels: a product's parentId is its raw
    // ss_applicableto ("100000003,100000004") and the investor type's is its own code.
    const wanted = selectedType?.parentId?.trim();
    if (!wanted) return all;
    const matching = all.filter(p =>
      (p.parentId || '')
        .split(/[,;]/)
        .map(v => v.trim())
        .filter(Boolean)
        .includes(wanted),
    );
    // A product with no applicability recorded should not disappear from the list.
    const unscoped = all.filter(p => !p.parentId || !p.parentId.trim());
    return [...matching, ...unscoped];
  }, [lookups, selectedType]);

  // Every plan the firm offers, whatever the product: the Dataverse lookup is not narrowed by
  // product, so narrowing it here would hide plans the portal would let you pick.
  const plans: IntroOption[] = useMemo(() => lookups?.plans || [], [lookups]);

  // Clear dependent choices when their parent changes, so a stale pair can never be submitted.
  useEffect(() => {
    if (form.productGuid && !products.some(p => p.id === form.productGuid)) {
      setForm(f => ({ ...f, productGuid: '', planGuid: '' }));
    }
  }, [products]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form.planGuid && !plans.some(p => p.id === form.planGuid)) {
      setForm(f => ({ ...f, planGuid: '' }));
    }
  }, [plans]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.investorTypeGuid) e.investorTypeGuid = 'Select an investor type';
    if (!form.nationalityGuid) e.nationalityGuid = 'Select a nationality';
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email address';
    if (!form.mobile.trim()) e.mobile = 'Mobile number is required';
    else if (!/^[0-9]{6,15}$/.test(form.mobile.trim())) e.mobile = 'Enter 6 to 15 digits';
    if (!form.investmentRouteGuid) e.investmentRouteGuid = 'Select an investment route';
    if (!form.productGuid) e.productGuid = 'Select a product';
    if (!form.brokerBankGuid) e.brokerBankGuid = 'Select a preferred bank';
    if (!form.planGuid) e.planGuid = 'Select a brokerage plan';
    if (form.useFacilonServiceAgent && !form.facilonServiceAgentGuid) {
      e.facilonServiceAgentGuid = 'Choose a service agent, or set this to No';
    }
    // The server enforces this too; stopping here saves a round trip and points at the field.
    if (emailCheck?.status === 'DUPLICATE_PRODUCT') {
      e.productGuid = 'This investor already has this product';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error('Please correct the highlighted fields');
      return;
    }
    setSaving(true);
    try {
      const payload: IntroduceInvestorRequest = {
        investorTypeGuid: form.investorTypeGuid,
        nationalityGuid: form.nationalityGuid,
        countryOfResidenceGuid: form.countryOfResidenceGuid || null,
        firstName: form.firstName.trim(),
        middleName: form.middleName.trim() || null,
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        isdCodeGuid: form.isdCodeGuid || null,
        mobile: form.mobile.trim(),
        useFacilonServiceAgent: form.useFacilonServiceAgent,
        facilonServiceAgentGuid: form.useFacilonServiceAgent ? form.facilonServiceAgentGuid : null,
        investmentRouteGuid: form.investmentRouteGuid,
        productGuid: form.productGuid,
        brokerBankGuid: form.brokerBankGuid,
        planGuid: form.planGuid,
      };
      const created = await serviceAgentIntroductionService.introduce(payload);
      toast.success(`${created.investorName} introduced as ${created.code}`);
      navigate('/service-agent/introduced-investors', { state: { justCreated: created.id } });
    } catch (err: any) {
      const message = err?.response?.data?.message;
      toast.error(message || 'The investor could not be introduced. Nothing was saved.');
    } finally {
      setSaving(false);
    }
  };


  if (!loading && blocked) {
    return (
      <div className="facilon-dashboard-wrapper font-sans text-gray-800 min-h-screen">
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-4 max-w-2xl">
          <h2 className="text-[15px] font-bold text-slate-800 mb-2">
            <i className="bi bi-exclamation-triangle text-[#b45309] mr-2"></i>
            Introductions are not available for your profile
          </h2>
          <p className="text-[12px] text-slate-600 mb-3">{blocked}</p>
          <button className="btn btn-sm btn-secondary" onClick={() => navigate('/service-agent/dashboard')}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  /** Only nationalities put a searchable code in parentId; elsewhere it is a grouping key. */
  const asOptions = (list: IntroOption[] | undefined, withKeywords = false): SearchableOption[] =>
    (list || []).map(o => ({ id: o.id, name: o.name, keywords: withKeywords ? o.parentId : null }));

  const err = (k: string) => (errors[k] ? <div className="invalid-feedback d-block text-[11px]">{errors[k]}</div> : null);
  const cls = (k: string) => `form-control form-control-sm${errors[k] ? ' is-invalid' : ''}`;

  return (
    <div className="sa-introduce facilon-dashboard-wrapper font-sans text-gray-800 min-h-screen pb-0">
      {/* Centred logo loader over a translucent backdrop: the form stays visible (and mounted,
          with its values intact if Power Apps rejects the write) — only the logo and ring
          animate in the middle, rather than the whole page turning teal. */}
      {saving && (
        <div className="sa-introduce__saving" role="status" aria-live="polite">
          <div className="loading-brand">
            <div className="loading-ring"></div>
            <img className="loading-logo" src="/assets/images/logo.png" alt="" />
          </div>
          <p>Creating the investor in Power Apps…</p>
        </div>
      )}
      <main className="container-fluid px-0 pt-0 pb-0 dashboard-container-main">

        {/* Banner */}
        <div className="bg-gradient-to-r from-[#2c5e6a] to-[#355f69] text-white rounded shadow-sm mb-2 px-4 py-2.5 flex justify-between items-center flex-wrap gap-2">
          <div>
            <h1 className="text-[16px] font-bold text-white mb-0">Introduce Investor</h1>
            <p className="text-[12px] opacity-90 mt-1 mb-0">
              Create an introduction for your service provider. The investor is emailed a registration link.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/service-agent/introduced-investors')}
            className="bg-white/10 border border-white/25 rounded px-2.5 py-1 text-[11px] cursor-pointer hover:bg-white/20 transition-colors"
          >
            My Introductions
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2"><div className="py-5 text-center"><Spinner animation="border" variant="primary" /></div></div>
        ) : (<>
        {/* Who this is being created for — resolved server-side, shown so the agent can check it */}
        <div className="bg-[#f1f7f8] border border-[#cfe0e4] rounded-lg px-3 py-2 mb-2 flex flex-wrap items-center gap-x-7 gap-y-1">
          <div>
            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Introducing on behalf of</div>
            <div className="text-[12px] font-semibold text-slate-800">
              {lookups?.serviceProviderName}
              {lookups?.serviceProviderCode && (
                <span className="text-[11px] text-slate-500 font-normal ml-2">{lookups.serviceProviderCode}</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Client Executive Agent</div>
            <div className="text-[12px] font-semibold text-slate-800">
              {lookups?.clientExecutiveAgentName}
              <span className="text-[11px] text-slate-500 font-normal ml-2">(you)</span>
            </div>
          </div>
          <div className="text-[10.5px] text-slate-500 ml-auto max-w-[240px] leading-snug">
            Both come from your agent profile and are applied automatically.
          </div>
        </div>

        <form onSubmit={submit} noValidate>

          {/* Provide Investor Details */}
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
            <div className="sa-introduce__section-head">
              <span>Provide Investor Details</span>
            </div>
            <div className="sa-introduce__grid">
              <div className="form-group">
                <label htmlFor="investorType">
                  Investor Type <span className="text-danger">*</span>
                </label>
                <SearchableSelect id="investorType" options={asOptions(investorTypes)}
                        value={form.investorTypeGuid} isInvalid={!!errors.investorTypeGuid}
                        onChange={v => set('investorTypeGuid', v)} />
                {err('investorTypeGuid')}
              </div>
              <div className="form-group">
                <label htmlFor="nationality">
                  Nationality <span className="text-danger">*</span>
                </label>
                <SearchableSelect id="nationality" options={asOptions(lookups?.nationalities, true)}
                        value={form.nationalityGuid} isInvalid={!!errors.nationalityGuid}
                        onChange={v => set('nationalityGuid', v)} />
                {err('nationalityGuid')}
              </div>
              <div className="form-group">
                <label htmlFor="residence">Country of Residence</label>
                <SearchableSelect id="residence" options={asOptions(lookups?.countriesOfResidence)}
                        value={form.countryOfResidenceGuid}
                        onChange={v => set('countryOfResidenceGuid', v)} />
              </div>
            </div>
          </div>

          {/* Investor Details */}
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
            <div className="sa-introduce__section-head">
              <span>Investor Details</span>
            </div>
            <div className="sa-introduce__grid">
              <div className="form-group">
                <label htmlFor="firstName">
                  First Name <span className="text-danger">*</span>
                </label>
                <input id="firstName" maxLength={100} className={cls('firstName')} value={form.firstName}
                       onChange={e => set('firstName', e.target.value)} />
                {err('firstName')}
              </div>
              <div className="form-group">
                <label htmlFor="middleName">Middle Name</label>
                <input id="middleName" maxLength={100} className="form-control form-control-sm" value={form.middleName}
                       onChange={e => set('middleName', e.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">
                  Last Name <span className="text-danger">*</span>
                </label>
                <input id="lastName" maxLength={100} className={cls('lastName')} value={form.lastName}
                       onChange={e => set('lastName', e.target.value)} />
                {err('lastName')}
              </div>

              <div className="form-group form-group--wide">
                <label htmlFor="email">
                  Email <span className="text-danger">*</span>
                </label>
                <input id="email" maxLength={190} type="email" className={cls('email')} value={form.email}
                       onChange={e => set('email', e.target.value)} />
                {err('email')}
                {!emailCheck && (
                  <div className="form-text text-[11px]">
                    {checkingEmail ? 'Checking this email…' : 'The registration link is sent here.'}
                  </div>
                )}
                {emailCheck && (
                  <div
                    className={`sa-introduce__notice${emailCheck.status === 'DUPLICATE_PRODUCT' ? ' is-blocking' : ''}`}
                    role={emailCheck.status === 'DUPLICATE_PRODUCT' ? 'alert' : 'status'}
                  >
                    <i
                      className={`bi ${emailCheck.status === 'DUPLICATE_PRODUCT' ? 'bi-exclamation-triangle' : 'bi-info-circle'}`}
                      aria-hidden="true"
                    ></i>
                    <span>{emailCheck.message}</span>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="isd">Country of Residence (ISD Code)</label>
                <SearchableSelect id="isd" options={asOptions(lookups?.isdCodes)}
                        value={form.isdCodeGuid} onChange={v => set('isdCodeGuid', v)} />
              </div>
              <div className="form-group">
                <label htmlFor="mobile">
                  Mobile Phone <span className="text-danger">*</span>
                </label>
                <input id="mobile" maxLength={15} inputMode="numeric" className={cls('mobile')} value={form.mobile}
                       onChange={e => set('mobile', e.target.value.replace(/[^0-9]/g, ''))} />
                {err('mobile')}
              </div>

              {/* Not shown: "Use Facilon Service Agent?" is always No, and the Client
                  Executive Agent is always the signed-in agent. Both are fixed by the
                  server from the agent's profile; the form sends the defaults. */}
            </div>
          </div>

          {/* Investor Product */}
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
            <div className="sa-introduce__section-head">
              <span>Investor Product</span>
            </div>
            <div className="sa-introduce__grid">
              <div className="form-group">
                <label htmlFor="route">
                  Investment Route <span className="text-danger">*</span>
                </label>
                <SearchableSelect id="route" options={asOptions(lookups?.investmentRoutes)}
                        value={form.investmentRouteGuid} isInvalid={!!errors.investmentRouteGuid}
                        onChange={v => set('investmentRouteGuid', v)} />
                {err('investmentRouteGuid')}
              </div>
              <div className="form-group form-group--wide">
                <label htmlFor="product">
                  Product <span className="text-danger">*</span>
                </label>
                <SearchableSelect id="product" options={asOptions(products)}
                        value={form.productGuid} isInvalid={!!errors.productGuid}
                        onChange={v => set('productGuid', v)} />
                {err('productGuid')}
                <div className="form-text text-[11px]">
                  {selectedType ? `Available to ${selectedType.name} investors at your firm.` : 'Products your firm offers.'}
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="bank">
                  Broker Preferred Bank <span className="text-danger">*</span>
                </label>
                <SearchableSelect id="bank" options={asOptions(lookups?.brokerBanks)}
                        value={form.brokerBankGuid} isInvalid={!!errors.brokerBankGuid}
                        onChange={v => set('brokerBankGuid', v)} />
                {err('brokerBankGuid')}
              </div>
              <div className="form-group form-group--wide">
                <label htmlFor="plan">
                  Brokerage Plan <span className="text-danger">*</span>
                </label>
                <SearchableSelect id="plan" options={asOptions(plans)} value={form.planGuid}
                        isInvalid={!!errors.planGuid}
                        onChange={v => set('planGuid', v)} />
                {err('planGuid')}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] px-3 py-2 mb-2 d-flex align-items-center flex-wrap gap-2">
            <div className="text-[10.5px] me-auto" style={{ maxWidth: 520 }}
                 role={saving ? 'status' : undefined} aria-live={saving ? 'polite' : undefined}>
              {saving ? (
                <span className="text-[#2c5e6a] fw-semibold">
                  <span className="spinner-border spinner-border-sm me-2" style={{ width: 12, height: 12 }} aria-hidden="true"></span>
                  Creating the investor in Power Apps. This can take a few seconds while it sets up the contact and journey — please don&rsquo;t reload.
                </span>
              ) : (
                <span className="text-slate-500">
                  On save the investor record is created in Power Apps and the introduction email goes out within a minute.
                </span>
              )}
            </div>
            <button type="button" className="btn btn-sm btn-secondary" disabled={saving}
                    onClick={() => navigate('/service-agent/dashboard')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-sm btn-primary"
                    disabled={saving || emailCheck?.status === 'DUPLICATE_PRODUCT'}>
              {saving ? 'Introducing…' : 'Introduce Investor'}
            </button>
          </div>
        </form>
        </>)}
      </main>
    </div>
  );
};

export default IntroduceInvestor;
